/**
 * Shared vocabulary for proposal surfaces (2026-09-20).
 *
 * Why this file exists: the owner's inbox showed two byte-identical
 * "Daily reflection awaiting approval" cards and asked "how can I approve
 * without seeing them" — the honest answer being that they could not. The
 * fix has three surfaces (the /inbox card, the /proposals card, and the
 * dock inspector that opens from either), and Rule 21 has already recorded
 * three separate drifts caused by per-surface copies of per-kind truth.
 * One module, one shape.
 */
import type { Remedy } from "../components/common/FailureExplanation";
import type { DockPanel } from "../stores/dock";

/** One proposal's reviewable document, as `GET /api/proposals/{id}/preview`
 *  returns it. The approve/reject URLs come from the row the server built,
 *  so a viewer can never offer a different verb than the card that opened
 *  it (the hand-kept per-kind table this deliberately avoids). */
export interface ProposalPreview {
	id: string;
	kind: string;
	title: string;
	path: string;
	content: string;
	approve_url: string;
	reject_url: string;
	/** Rule 26 — why this is in front of you. The SAME block the list card
	 *  renders, stamped server-side by the shared annotator, so opening the
	 *  document cannot re-label its own buttons. */
	cause?: string;
	semantics?: {
		approve?: { verb?: string; effect?: string };
		reject?: { verb?: string; effect?: string };
		remedies?: Remedy[];
	};
}

/** Thrown when the proposal is no longer pending — a NORMAL outcome
 *  (someone approved it elsewhere), distinguished from a real failure so
 *  the viewer can say which happened (Rule 26). */
export class ProposalGone extends Error {}

export async function fetchProposalPreview(id: string): Promise<ProposalPreview> {
	const res = await fetch(`/api/proposals/${encodeURIComponent(id)}/preview`);
	if (res.status === 404) {
		const detail = await res
			.json()
			.then((b) => String(b?.detail ?? ""))
			.catch(() => "");
		throw new ProposalGone(
			detail ||
				"That proposal is no longer pending — it may have been decided already.",
		);
	}
	if (!res.ok) throw new Error(`Couldn't load the proposal (${res.status})`);
	return (await res.json()) as ProposalPreview;
}

/** Fired (on window) whenever a proposal is approved or rejected from ANY
 *  surface, so /inbox and /proposals reload their lists. Same bus the rest
 *  of the app uses (`agntspace:*` CustomEvents). */
export const PROPOSALS_CHANGED_EVENT = "agntspace:proposals-changed";

export function emitProposalsChanged(): void {
	window.dispatchEvent(new CustomEvent(PROPOSALS_CHANGED_EVENT));
}

/**
 * Soft-fail from kinds that keep the card open when the click did not close
 * the gap. Canonical case: `market_data_gap` approve returns HTTP 200 with
 * `closed: false` + a message (no provider answered / nothing stored). The
 * CLI already prints that as a warning and refuses to say "done"; the UI
 * used to ignore it, so Fetch looked like a dead button (card still there,
 * no explanation). Rule 16 / Rule 29 — a 200 that stored nothing is not
 * success, and silence is worse than the gap.
 *
 * Returns the server's message when the card MUST stay open, else null.
 */
export function proposalActionKeptOpen(body: unknown): string | null {
	if (!body || typeof body !== "object") return null;
	const b = body as Record<string, unknown>;
	if (b.closed !== false) return null;
	const msg = typeof b.message === "string" ? b.message.trim() : "";
	return msg || "Not closed — the gap is unchanged.";
}

/** Panel id <-> proposal id, so /proposals and /inbox can keep the open
 *  proposal in the URL (`?proposal=<id>`, hooks/useInspectorUrl) without
 *  re-deriving the id format the builder below owns. */
export const proposalPanelId = (id: string) => `proposal:${id}`;
export function proposalIdOfPanel(panel: DockPanel): string | null {
	return panel.id.startsWith("proposal:") ? panel.id.slice("proposal:".length) : null;
}

/** Build the dock panel for one proposal. The id is stable per proposal, so
 *  clicking the same card focuses its existing tab instead of cloning
 *  (openPanel's add-or-focus contract), while two different proposals get
 *  their own tabs — which is the whole point when the five cards you are
 *  triaging used to be indistinguishable. */
export function proposalInspectorPanel(id: string, title: string): DockPanel {
	return {
		id: proposalPanelId(id),
		kind: "inspector",
		title,
		data: { inspector: "proposal", props: { proposalId: id } },
		// No `scope` — deliberately. A scoped panel renders only inside its
		// own module, and this one opens from BOTH /inbox and /proposals;
		// scoping it to either would make the review vanish when the user
		// moved to the other. Scope-less panels render everywhere.
	};
}
